import { Box } from '@mui/material';
import Sidebar from './Sidebar/Sidebar';
import { useEffect, useState } from 'react';
import { useAuthContext } from 'src/Core/hooks/AuthContext';
import LayoutHeader from 'src/Core/Layout/Header/LayoutHeader';
import { MAELABORD_REPORTS } from 'src/Maelaboard/reports';
import { useIsMobile } from '../hooks/useIsMobile';
import { useNavigation } from '../hooks/NavigationContext';
import { env } from 'src/Core/env.mjs';
import { api } from 'pages/api/trpc/_api';

export interface Layout {
  children: React.ReactNode;
}

export default function LayoutCore({ children }: Layout) {
  const [sidebarWidth] = useState(255);
  const isMobile = useIsMobile();
  const { hasAccessToUmsysla, hasAccessToClaimManagement, hasAccessToDashboards, isInternalUser, hasAccessToVinnsla } =
    useAuthContext();
  const { setHasNavigated } = useNavigation();

  const { data: debtEvalClaims } = api.taktuAkvordun.getClaims.useQuery(undefined, {
    enabled: hasAccessToClaimManagement,
    staleTime: 5 * 60 * 1000,
  });
  const { data: closureData } = api.taktuAkvordun.getClosureClaims.useQuery(undefined, {
    enabled: hasAccessToClaimManagement,
    staleTime: 5 * 60 * 1000,
  });
  const debtEvalCount = debtEvalClaims?.filter((c) => !c.markedInReview).length ?? 0;
  const closureCount = (closureData?.closureRecommended.length ?? 0) + (closureData?.closureInProgress.length ?? 0);
  const taktuAkvordunCount = debtEvalCount + closureCount;

  useEffect(() => {
    setHasNavigated(true);
  }, []);

  const handleListOfSidebarItems = () => {
    return [
      { name: 'Forsíða', path: '/' },
      {
        name: 'Kröfuþjónusta',
        path: '/krofustyring',
        childItems: [
          ...(env.ENV !== 'production'
            ? [
                { name: 'Taktu ákvörðun', path: '/krofustyring/taktu-akvordun', badge: taktuAkvordunCount },
                { name: 'Innheimta', path: '/krofustyring/innheimta' },
              ]
            : []),
          { name: 'Kröfustofnun', path: '/krofustyring/yfirlit' },
          ...(env.ENV !== 'production' ? [{ name: 'Greiðendur', path: '/krofustyring/greidendur' }] : []),
        ],
        isDisabled: !hasAccessToClaimManagement,
      },
      ...(hasAccessToDashboards || isInternalUser
        ? [
            {
              name: 'Mælaborð',
              path: '/maelaboard',
              childItems: Object.values(MAELABORD_REPORTS)
                .filter((report) => (report.audience === 'internal' ? isInternalUser : hasAccessToDashboards))
                .map(({ name, path }) => ({ name, path })),
            },
          ]
        : []),
      // Dagatal is not yet ready for production
      ...(env.ENV !== 'production'
        ? [
            {
              name: 'Dagatal',
              path: '/krofustyring/dagatal',
              isDisabled: !hasAccessToClaimManagement,
            },
          ]
        : []),
      {
        name: 'Vinnsla',
        path: '/vinnsla',
        childItems: [
          { name: 'Vinnslulisti', path: '/vinnsla/vinnslulisti' },
          { name: 'Vanskilaskráning', path: '/vinnsla/vanskilaskraning' },
        ],
        isDisabled: !hasAccessToVinnsla,
      },
      {
        name: 'Umsýsla',
        path: '/umsysla',
        childItems: [
          { name: 'Aðgangsstýring', path: '/umsysla/adgangsstyring' },
          { name: 'Auðkenna notendur', path: '/umsysla/audkennaNotendur' },
        ],
        isDisabled: !hasAccessToUmsysla,
      },
    ];
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      {!isMobile && <Sidebar items={handleListOfSidebarItems()} sidebarWidth={sidebarWidth} onExpandClick={() => {}} />}
      <Box
        className="app-content"
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: `${isMobile ? 0 : sidebarWidth}px`,
        }}
      >
        {isMobile && <LayoutHeader sidebarItems={handleListOfSidebarItems()} />}
        <Box className="app-content-inner" sx={{ minHeight: '70vh', flex: '1 0 auto' }}>
          <Box sx={{ p: 3 }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}
